import { ErrorBoundary, useConfirm } from '@genial/components';
import { Color } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Center } from 'native-base';
import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../routes/types';
import { useRateManagementActions } from '../../../store/rateManagement/actions';
import {
  rateManagementCancelOrderState,
  rateManagementSendOrderState,
} from '../../../store/rateManagement/atom';
import {
  SentOrderDetailResponseNormalized,
  sentOrderDetailQuery,
} from '../../../store/rateManagement/selector';

import { TableCustomerDetailTypes } from './components/TableCustomerDetail/table';
import { RequestDetailsDesktop } from './Desktop';
import { RequestDetailsMobile } from './Mobile';
import { RequestDetailsTablet } from './Tablet';

export namespace RequestDetailsTypes {
  export interface Props {
    onCloseDetails: () => void;
    onCancelOrder: () => void;
    onSendOrder: () => void;
    onSelectTable: (table: TableCustomerDetailTypes.TableSelected) => void;
    onExport: () => void;
    tableSelected: TableCustomerDetailTypes.TableSelected;
    sentOrder?: SentOrderDetailResponseNormalized | null;
    loading?: boolean;
    error?: Error;
    isValidCustomersEmpty?: boolean;
    isNeedingAttentionEmpty?: boolean;
  }
}

export const RequestDetails: React.FC<
  NativeStackScreenProps<SignedInRootStackParamList, 'RequestDetails'>
> = ({ route }) => {
  const { sentOrderId } = route.params;
  const { data, loading, error, refresher } = useRecoilQuery(
    sentOrderDetailQuery({ sentOrderId }),
    { cachePolicy: 'no-cache' }
  );
  const { goBack, canGoBack, navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const {
    resetCancelOrderState,
    resetSendOrderState,
    cancelOrder,
    notifyOrderSent,
    notifyOrderCancelled,
    exportOrderDetailsPdf,
  } = useRateManagementActions();
  const { isCanceled } = useRecoilValue(rateManagementCancelOrderState);
  const { isSent } = useRecoilValue(rateManagementSendOrderState);
  const { confirm } = useConfirm();

  const [allData, setAllData] = useState<SentOrderDetailResponseNormalized | null | undefined>();
  const [tableSelected, setTableSelected] =
    useState<TableCustomerDetailTypes.TableSelected>('valid');

  const isValidCustomersEmpty = allData ? allData.validCustomers.length === 0 : true;
  const isNeedingAttentionEmpty = allData ? allData.invalidCustomers.length === 0 : true;

  const isMobile = useBreakpointValue({ base: true, sm: false });
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();

  const handleCloseDetails = () => {
    if (canGoBack()) {
      return goBack();
    }

    navigate('RateManagement');
  };

  const handleCancelOrder = () => {
    confirm(
      {
        isMobile,
        description:
          'Esta ação limpará os dados salvos até o momento. Deseja continuar mesmo assim?',
        confirmText: 'Continuar',
        confirmColor: 'feedback.error.default',
      },
      () => {
        cancelOrder(sentOrderId);
      }
    );
  };

  const handleSendOrder = useCallback(() => {
    if (canGoBack()) {
      goBack();
    }
    navigation.navigate('ConfirmMasterAccount', {
      orderId: sentOrderId,
      managerAccountNumber: allData?.lastManagerAccountNumber.toString() || '',
      managerAccountDigit: allData?.lastManagerAccountDigit || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, allData]);

  const handleExport = () => {
    exportOrderDetailsPdf(data);
  };

  const handleSelectTable = (table: TableCustomerDetailTypes.TableSelected) => {
    setTableSelected(table);
  };

  useEffect(() => {
    if (!loading && data) {
      setAllData(data);
    }
  }, [data, loading]);

  useEffect(() => {
    return () => {
      resetCancelOrderState();
      resetSendOrderState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isCanceled) {
      notifyOrderCancelled();
      handleCloseDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCanceled]);

  useEffect(() => {
    if (isSent) {
      notifyOrderSent();
      handleCloseDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSent]);

  useEffect(() => {
    setTableSelected(isValidCustomersEmpty ? 'requiresAttention' : 'valid');
  }, [isValidCustomersEmpty]);

  const content = useBreakpointValue({
    base: (
      <RequestDetailsMobile
        onCloseDetails={handleCloseDetails}
        onCancelOrder={handleCancelOrder}
        onSendOrder={handleSendOrder}
        onSelectTable={handleSelectTable}
        onExport={handleExport}
        sentOrder={allData}
        loading={loading}
        error={error}
        isValidCustomersEmpty={isValidCustomersEmpty}
        isNeedingAttentionEmpty={isNeedingAttentionEmpty}
        tableSelected={tableSelected}
      />
    ),
    sm: (
      <RequestDetailsTablet
        onCloseDetails={handleCloseDetails}
        onCancelOrder={handleCancelOrder}
        onSendOrder={handleSendOrder}
        onSelectTable={handleSelectTable}
        onExport={handleExport}
        sentOrder={allData}
        loading={loading}
        error={error}
        isValidCustomersEmpty={isValidCustomersEmpty}
        isNeedingAttentionEmpty={isNeedingAttentionEmpty}
        tableSelected={tableSelected}
      />
    ),
    md: (
      <RequestDetailsDesktop
        onCloseDetails={handleCloseDetails}
        onCancelOrder={handleCancelOrder}
        onSendOrder={handleSendOrder}
        onSelectTable={handleSelectTable}
        onExport={handleExport}
        sentOrder={allData}
        loading={loading}
        error={error}
        isValidCustomersEmpty={isValidCustomersEmpty}
        isNeedingAttentionEmpty={isNeedingAttentionEmpty}
        tableSelected={tableSelected}
      />
    ),
  });

  if (error) {
    return (
      <Center flex={1} backgroundColor={Color['neutral.white']}>
        <ErrorBoundary onRefresh={refresher} />
      </Center>
    );
  }

  return content;
};
